// DESIGN.md "Szövegírás": a number comes with what it is measured against.
// These build that second line; they never replace the page's own number
// formatting.

/** `part` as a share of `whole`: "52%", "0,4%". Null when there is no whole
 * to compare with — a share of nothing is not zero, it is not a share. */
export function share(part: number | null | undefined, whole: number | null | undefined): string | null {
  if (part === null || part === undefined || whole === null || whole === undefined || whole <= 0) {
    return null;
  }
  const ratio = (part / whole) * 100;
  // A complaint rate is read in hundredths of a percent (0,1% is the rule-4
  // line); a delivery rate in whole percents.
  const size = Math.abs(ratio);
  const digits = ratio === 0 ? 0 : size < 1 ? 2 : size < 10 ? 1 : 0;
  return `${ratio.toLocaleString("hu-HU", { maximumFractionDigits: digits })}%`;
}

/** "a címzettek 97%-a" — or null when there is nothing to compare with. */
export function shareOf(
  part: number | null | undefined,
  whole: number | null | undefined,
  what: string,
): string | null {
  const text = share(part, whole);
  return text === null ? null : `${what} ${text}-a`;
}

// --- Counts and moments (UI-2) -----------------------------------------------

/** "1 234 567" — or "—" where there is no number, which is not the same as 0. */
export function count(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : value.toLocaleString("hu-HU");
}

const TO_THE_MINUTE: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
};

/** A moment to the minute, "2026. 09. 17. 12:34" — in the reader's own zone,
 * or in `timeZone` when the moment belongs to a country (a send time is read
 * on the recipient's wall clock). "—" for no moment. The seconds are in the
 * logs; on a screen they only make a column wider. */
export function minute(value: string | null | undefined, timeZone?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  try {
    return date.toLocaleString("hu-HU", timeZone ? { ...TO_THE_MINUTE, timeZone } : TO_THE_MINUTE);
  } catch {
    // An unknown zone name: the reader's own zone beats no answer.
    return date.toLocaleString("hu-HU", TO_THE_MINUTE);
  }
}
